import {
  mockAuditEvents,
  mockDashboard,
  mockModelRequestDetails,
  mockModelRequests,
  mockTraceDetails,
  mockTraces,
} from './mock-data';
import type {
  AuditListResponse,
  DashboardData,
  ModelHttpRequestDetail,
  ModelHttpRequestListResponse,
  PlatformStatus,
  TraceDetail,
  TraceDetailView,
  TraceFilters,
  TraceListResponse,
  TraceListItem,
  ViewerIdentity,
} from '../types';
import { toTraceDetailView } from '../types';

const API_ROOT = '/api';
const allowMockFallback = import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS !== 'false';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_ROOT}${path}`, {
    ...(signal ? { signal } : {}),
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    let code: string | undefined;
    let message = `请求失败（HTTP ${response.status}）`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code;
      message = body.error?.message ?? message;
    } catch {
      // Preserve the status-only fallback for non-JSON edge responses.
    }
    throw new ApiError(message, response.status, code);
  }
  return (await response.json()) as T;
}

export async function getAudit(signal?: AbortSignal): Promise<AuditListResponse> {
  try {
    return await request<AuditListResponse>('/audit?limit=50', signal);
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      return { items: mockAuditEvents };
    }
    throw error;
  }
}

export async function getModelRequests(signal?: AbortSignal): Promise<ModelHttpRequestListResponse> {
  try {
    return await request<ModelHttpRequestListResponse>('/model-requests', signal);
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      return { items: mockModelRequests, total: mockModelRequests.length };
    }
    throw error;
  }
}

export async function getModelRequestsByRunId(runId: string, signal?: AbortSignal): Promise<ModelHttpRequestListResponse> {
  try {
    return await request<ModelHttpRequestListResponse>(`/model-requests?runId=${encodeURIComponent(runId)}`, signal);
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      const items = mockModelRequests.filter((item) => item.runId === runId);
      return { items, total: items.length };
    }
    throw error;
  }
}

export async function getModelRequestDetail(id: number, signal?: AbortSignal): Promise<ModelHttpRequestDetail> {
  try {
    return await request<ModelHttpRequestDetail>(`/model-requests/${id}`, signal);
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      const detail = mockModelRequestDetails[id];
      if (!detail) throw new ApiError('找不到这条模型请求', 404);
      return detail;
    }
    throw error;
  }
}

const filterMockTraces = (filters: TraceFilters): TraceListItem[] =>
  mockTraces.filter((trace) => {
    if (filters.roomId && !trace.roomId.toLowerCase().includes(filters.roomId.toLowerCase())) return false;
    if (filters.runId && !trace.runId.toLowerCase().includes(filters.runId.toLowerCase())) return false;
    if (filters.playerId && !trace.playerId?.toLowerCase().includes(filters.playerId.toLowerCase())) return false;
    if (filters.source && trace.source !== filters.source) return false;
    if (filters.status && trace.status !== filters.status) return false;
    return true;
  });

export async function getDashboard(signal?: AbortSignal): Promise<DashboardData> {
  try {
    const [viewer, status, traces] = await Promise.all([
      request<ViewerIdentity>('/viewer', signal),
      request<PlatformStatus>('/status', signal),
      request<TraceListResponse>('/traces', signal),
    ]);
    return { viewer, status, traces };
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      return mockDashboard;
    }
    throw error;
  }
}

export async function searchTraces(filters: TraceFilters, signal?: AbortSignal): Promise<TraceListResponse> {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value != null && value !== '') query.set(key, String(value));
  });

  try {
    return await request<TraceListResponse>(`/traces${query.size ? `?${query}` : ''}`, signal);
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      const items = filterMockTraces(filters);
      return { items, total: items.length };
    }
    throw error;
  }
}

export async function getTraceDetail(id: string, signal?: AbortSignal): Promise<TraceDetailView> {
  try {
    const detail = await request<TraceDetail>(`/traces/${encodeURIComponent(id)}`, signal);
    return toTraceDetailView(detail);
  } catch (error) {
    if (allowMockFallback && !(error instanceof DOMException && error.name === 'AbortError')) {
      const detail = mockTraceDetails[id];
      if (!detail) throw new ApiError('找不到这条 Trace', 404);
      return toTraceDetailView(detail);
    }
    throw error;
  }
}
